import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Read-aloud (text-to-speech OUTPUT) for the drawing hints, so pre-readers can
// use the app by ear: each step's instruction — "This is a CIRCLE!", "Now
// colour it in!" — is spoken as it appears. Uses the browser's built-in Web
// Speech API (SpeechSynthesis); no library, no network, no cost.
//
// This is deliberately separate from the SoundProvider (the little musical
// blips) and the VoiceProvider (speech *recognition*, i.e. listening): a grown-
// up may want the chirps but not a voice, or the voice but not the chirps.

import { clipUrl } from "./clips";
import { useLocale, useT } from "../i18n/LocaleProvider";
import { LOCALE_META } from "../i18n/locale";

import { holdMic, releaseMic } from "./micGate";

const READALOUD_KEY = "learn-draw:readaloud";

// Speak an utterance while holding the mic suspended (so voice control can't
// hear the speaker), releasing exactly once when it ends. A fallback timer
// guarantees the release even on browsers that skip `end`/`error` when an
// utterance is cancelled (Safari), so the mic can never get stuck off. The
// estimate is generous — utterances here are short kid hints.
function speakHeld(s: SpeechSynthesis, u: SpeechSynthesisUtterance) {
  holdMic();
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    clearTimeout(fallback);
    releaseMic();
  };
  const estimateMs = Math.min(20000, u.text.length * 120 + 1500);
  const fallback = setTimeout(release, estimateMs);
  u.onend = release;
  u.onerror = release;
  s.speak(u);
}

// A 1-sample silent WAV, used to "unlock" the shared <audio> element inside the
// read-aloud toggle tap — browsers only allow later programmatic playback if the
// element has been played once during a user gesture.
const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=";

interface SpeakContextValue {
  /** Whether the browser can speak at all (Web Speech API present). */
  supported: boolean;
  /** Whether reading aloud is switched on. */
  enabled: boolean;
  toggle: () => void;
  /** Speak a phrase now (cancelling anything mid-sentence). No-op when off.
   *  Pass the current drawing's id so a drawing-specific clip can be preferred
   *  over the shared/common one (and over the browser voice). */
  speak: (text: string, drawingId?: string) => void;
  /** Stop any speech immediately (e.g. when leaving a screen). */
  cancel: () => void;
  /** Whether read-aloud is producing sound right now (a clip still playing or
   *  the browser voice mid-sentence). Auto Play uses this so a short step never
   *  advances before its narration has finished. Always false when read-aloud
   *  is off. */
  isSpeaking: () => boolean;
}

const SpeakContext = createContext<SpeakContextValue | null>(null);

function useSpeakCtx(): SpeakContextValue {
  const ctx = useContext(SpeakContext);
  if (!ctx) throw new Error("useSpeak must be used within a SpeakProvider");
  return ctx;
}

const synth = (): SpeechSynthesis | null =>
  typeof window !== "undefined" && "speechSynthesis" in window
    ? window.speechSynthesis
    : null;

function loadEnabled(): boolean {
  try {
    const v = localStorage.getItem(READALOUD_KEY);
    return v === null ? true : v === "1"; // read aloud on by default (toddler-first)
  } catch {
    return true;
  }
}

/** Turn a screen hint into something worth speaking: drop the emoji, and read a
 *  spelt-out word like "C-I-R-C-L-E" as separate letters ("C I R C L E") so the
 *  name-writing step is spelt aloud. */
function speakable(text: string): string {
  return text
    .replace(/[\p{Extended_Pictographic}‍️⃣]/gu, "") // emoji
    .replace(/(?<=\p{L})-(?=\p{L})/gu, " ") // C-I-R-C-L-E → C I R C L E (Greek too)
    .replace(/\s+/g, " ")
    .trim();
}

/** Prefer a natural local voice in the current language when one is available. */
function pickVoice(
  voices: SpeechSynthesisVoice[],
  langPattern: RegExp,
): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  const matching = voices.filter((v) => langPattern.test(v.lang));
  const pool = matching.length ? matching : voices;
  // A few voices that tend to sound warm/clear for kids, by name.
  const nice = pool.find((v) =>
    /samantha|google us english|karen|moira|libby|aria|jenny|amelie|audrey|thomas/i.test(
      v.name,
    ),
  );
  return nice ?? pool.find((v) => v.localService) ?? pool[0];
}

export function SpeakProvider({ children }: { children: ReactNode }) {
  const { locale, t } = useLocale();
  const { speechLang, voicePattern } = LOCALE_META[locale];
  const supported = synth() != null;
  const [enabled, setEnabled] = useState(loadEnabled);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Voices load asynchronously in some browsers — keep the latest list.
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  useEffect(() => {
    const s = synth();
    if (!s) return;
    const refresh = () => (voicesRef.current = s.getVoices());
    refresh();
    s.addEventListener?.("voiceschanged", refresh);
    return () => s.removeEventListener?.("voiceschanged", refresh);
  }, []);

  // One reused <audio> element for the pre-generated clips. Created lazily and
  // "unlocked" in the toggle tap so later programmatic plays are allowed.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Whether the clip element currently holds the mic suspended. A single flag
  // (not a count) keeps hold/release balanced no matter what order the audio
  // events arrive in.
  const audioHeldRef = useRef(false);
  // Monotonic id for the latest speak() request, so a late-rejecting play()
  // from a step we've already left can't trigger a stale browser-voice fallback.
  const speakSeqRef = useRef(0);
  const getAudio = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      const a = new Audio();
      // Suspend voice recognition while a clip is audible so the mic can't hear
      // the read-aloud and mistake it for a command — this is the MAIN output
      // path (nearly every hint has a Candy clip), so it must be gated just like
      // the browser-voice fallback. Idempotent hold/release keeps it balanced
      // across pause→play as the child moves between steps.
      const hold = () => {
        if (!audioHeldRef.current) {
          audioHeldRef.current = true;
          holdMic();
        }
      };
      const release = () => {
        if (audioHeldRef.current) {
          audioHeldRef.current = false;
          releaseMic();
        }
      };
      a.addEventListener("play", hold);
      a.addEventListener("playing", hold);
      a.addEventListener("ended", release);
      a.addEventListener("pause", release);
      a.addEventListener("error", release);
      audioRef.current = a;
    }
    return audioRef.current;
  }, []);

  // Speak a phrase with the browser's Web Speech voice (the always-available
  // fallback when no pre-generated clip exists, or a clip fails to play).
  const speakSynth = useCallback(
    (phrase: string) => {
      const s = synth();
      if (!s) return;
      s.cancel(); // never let hints pile up — always say the newest one
      const u = new SpeechSynthesisUtterance(phrase);
      u.rate = 0.95; // a touch slower for little ears
      u.pitch = 1.1; // gently bright and friendly
      u.lang = speechLang;
      const v = pickVoice(voicesRef.current, voicePattern);
      if (v) u.voice = v;
      speakHeld(s, u);
    },
    [speechLang, voicePattern],
  );

  // Unlock the <audio> element on the first user tap anywhere, so clips can play
  // later even if the child never toggles read-aloud (it defaults on). Browsers
  // gate programmatic audio behind a gesture; one silent play satisfies that.
  useEffect(() => {
    const unlock = () => {
      const a = getAudio();
      a.src = SILENT_WAV;
      a.play().then(() => a.pause()).catch(() => {});
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, [getAudio]);

  const cancel = useCallback(() => {
    synth()?.cancel();
    const a = audioRef.current;
    if (a) a.pause();
  }, []);

  const isSpeaking = useCallback(() => {
    if (!enabledRef.current) return false;
    const a = audioRef.current;
    if (a && !a.paused && !a.ended && a.currentTime > 0) return true;
    const s = synth();
    return !!(s && s.speaking);
  }, []);

  const speak = useCallback(
    (text: string, drawingId?: string) => {
      if (!enabledRef.current) return;
      const phrase = speakable(text);
      if (!phrase) return;
      const seq = ++speakSeqRef.current;
      synth()?.cancel(); // stop any half-spoken previous hint

      // Prefer the warm pre-generated clip when we have one for this phrase.
      const url = clipUrl(locale, drawingId, phrase);
      if (url) {
        const a = getAudio();
        a.pause();
        a.src = url;
        a.play().catch((err: DOMException) => {
          // Moving to the next step pauses this clip, which rejects play() with
          // an AbortError — that's not a failure, so DON'T fall back (that was
          // the double-voice / robot-voice bug). Only fall back for a genuine
          // load/playback failure on the step we're still on.
          if (err?.name === "AbortError") return;
          if (seq === speakSeqRef.current) speakSynth(phrase);
        });
        return;
      }
      speakSynth(phrase);
    },
    [getAudio, speakSynth, locale],
  );

  const toggle = useCallback(() => {
    setEnabled((on) => {
      const next = !on;
      try {
        localStorage.setItem(READALOUD_KEY, next ? "1" : "0");
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }
      synth()?.cancel();
      if (next) {
        // Confirm out loud in the SAME warm Candy voice as the hints (per locale),
        // not the device's default voice. Playing the clip within this tap also
        // unlocks the <audio> element for later clips. Only fall back to the
        // browser voice if the confirmation clip is somehow unavailable.
        const a = getAudio();
        const phrase = speakable(t.readAloudConfirm);
        const confirmUrl = clipUrl(locale, undefined, phrase);
        if (confirmUrl) {
          a.pause();
          a.src = confirmUrl;
          a.play().catch(() => {});
        } else {
          a.src = SILENT_WAV;
          a.play().then(() => a.pause()).catch(() => {});
          const s = synth();
          if (s) {
            const u = new SpeechSynthesisUtterance(phrase);
            u.rate = 0.95;
            u.pitch = 1.1;
            u.lang = speechLang;
            const v = pickVoice(voicesRef.current, voicePattern);
            if (v) u.voice = v;
            speakHeld(s, u);
          }
        }
      }
      return next;
    });
  }, [getAudio, t, locale, speechLang, voicePattern]);

  return (
    <SpeakContext.Provider
      value={{ supported, enabled, toggle, speak, cancel, isSpeaking }}
    >
      {children}
    </SpeakContext.Provider>
  );
}

/** Hook for screens that want to read text aloud (e.g. the drawing player). */
export function useSpeak(): SpeakContextValue {
  return useSpeakCtx();
}

/** The 🗣️/🤫 read-aloud on-off toggle. Hidden when the browser can't speak. */
export function SpeakButton() {
  const { supported, enabled, toggle } = useSpeakCtx();
  const t = useT();
  if (!supported) return null;
  return (
    <button
      className={enabled ? "sound-btn on" : "sound-btn"}
      onClick={toggle}
      aria-label={enabled ? t.readAloudOnAria : t.readAloudOffAria}
      aria-pressed={enabled}
    >
      {enabled ? "🗣️" : "🤫"}
      <span className="sound-btn-label">
        {enabled ? t.readAloudOn : t.readAloudOff}
      </span>
    </button>
  );
}
